import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, api, daysUntil, type Me, type MyWork as MyWorkPage, type MyWorkItem, type MyWorkShow, type MyWorkSummary } from '../lib/api'
import { countFrom, myWorkChanged } from '../lib/myWork'
import { GROUP_LABEL, TASK_NOUN, WAITING_FOR_YOU, taskVerb } from '../lib/status'
import { endQueue, startQueue } from '../lib/workQueue'
import '../styles/work-queue.css'
import { ChaseButton } from '../components/ChaseButton'
import { ErrorBox } from '../components/DataTable'
import { StatusChip } from '../components/StatusChip'
import { Skeleton, useToast } from '../components/ui'
import { dayMonth } from '../components/assess/journey'
import '../styles/home.css'
import '../styles/mywork.css'

// My work: everything waiting for the person signed in, in one list
// (release 3.2, design rule 9 and removal items 3, 4, 5 and 7).
//
// # WHY ONE LIST
//
// What waited for somebody was spread over Home's "Needs you" (eight kinds),
// the bell (a different set), Sign-offs, the badges on Offboarding, the
// Assessments banner and the Accepted risks tile, in eleven phrasings, and none
// was complete: vendor approvals, tier changes, replies to decide and the
// reader's own exit signatures were in none of them. The API now assembles
// every kind for this reader (GET /v1/my-work) and this screen shows it once.
//
// # EACH ROW DOES ONE THING
//
// A row names the thing, its state in the vocabulary's words, when it is due,
// and one button with the verb from lib/status.ts. The button either does the
// task here (Send reminder sends the invitation again; a notice is marked read)
// or opens exactly the screen where it is done: the assessment for answers, the
// exit workspace for a sign-off, the vendor's profile for an approval. No row
// opens a page that only shows the task again.
//
// # ONE FILTER, ONE PAGE AT A TIME
//
// The API filters, counts and pages (review of PR 24, M6): each filter is read
// on its own, fifty rows at a time with Show more for the rest, and the counts
// on the filter buttons are the API's, so the number on a button is the number
// of rows it opens. Notices are news, not tasks: they are a filter of their own,
// the newest listed and all of them counted, and are not in "All".
//
// # THE DEFAULT FILTER FOLLOWS THE JOB
//
// Opened without a filter (the landing), an approver lands on what waits for a
// decision, an analyst on reviews, a business owner on their own vendors, and
// everybody else on the whole list. A default that would show nothing falls
// back to the whole list, so nobody lands on an empty screen while work is
// waiting. Every count elsewhere (Home, the sidebar, the bell) opens the whole
// list, which is what it counts.

export type Show = MyWorkShow

const SHOWS: Show[] = ['all', 'decide', 'review', 'follow-up', 'mine', 'notice']

const SHOW_LABEL: Record<Show, string> = {
  all: 'All',
  decide: GROUP_LABEL.decide,
  review: GROUP_LABEL.review,
  'follow-up': GROUP_LABEL['follow-up'],
  notice: GROUP_LABEL.notice,
  mine: 'My vendors',
}

const PAGE = 50

// The filter each job lands on.
const PERSONA_SHOW: Record<string, Show> = {
  approver: 'decide',
  executive: 'decide',
  analyst: 'review',
  business_owner: 'mine',
  business_requester: 'mine',
  procurement: 'mine',
}

// One row per task. The API sends each task once; a repeated key would be the
// same task drawn twice, which is the defect this screen exists to end, so it
// is dropped here as well rather than trusted.
export function uniqueTasks(items: MyWorkItem[]): MyWorkItem[] {
  const seen = new Set<string>()
  const out: MyWorkItem[] = []
  for (const it of items) {
    if (seen.has(it.key)) continue
    seen.add(it.key)
    out.push(it)
  }
  return out
}

// The number on each filter button, from the API's counts: All is the tasks,
// every group but notices.
export function showCounts(sum: MyWorkSummary): Record<Show, number> {
  const c = sum.counts ?? ({} as MyWorkSummary['counts'])
  return {
    all: sum.total ?? 0,
    decide: c.decide ?? 0,
    review: c.review ?? 0,
    'follow-up': c['follow-up'] ?? 0,
    notice: c.notice ?? 0,
    mine: c.mine ?? 0,
  }
}

// The job's own filter, or the whole list when the job's filter is empty.
export function defaultShow(personaCode: string | undefined, counts: Record<Show, number>): Show {
  const want = PERSONA_SHOW[personaCode ?? ''] ?? 'all'
  return counts[want] > 0 ? want : 'all'
}

// The filter an address asks for, or null for none (the job's default).
export function showOf(hash: string): Show | null {
  const v = new URLSearchParams(hash.split('?')[1] ?? '').get('show')
  return SHOWS.includes(v as Show) ? (v as Show) : null
}

function writeShow(show: Show, byVendor: boolean) {
  // Replaced rather than pushed: a filter is a view of this page, and Back
  // should leave the page rather than step through each click. The filter is
  // always written, so a reload keeps it; so is the grouping, when it is on.
  history.replaceState(history.state, '', '#/my-work?show=' + show + (byVendor ? '&group=vendor' : ''))
}

// Whether the address asks for the rows grouped by vendor.
export function groupedOf(hash: string): boolean {
  return new URLSearchParams(hash.split('?')[1] ?? '').get('group') === 'vendor'
}

// The rows by vendor, in the order the list has them (the API's order: what is
// late first), a vendor's rows together under its name. A row about no one
// vendor (a bulk send, a notice about the programme) is in a group of its own
// at the end (3.3, lane 2: H2). A group is named by a task row about the
// vendor, never by a notice (a notice's title is its headline).
export function vendorGroups(items: MyWorkItem[]): { key: string; title: string; items: MyWorkItem[] }[] {
  const groups = new Map<string, { key: string; title: string; items: MyWorkItem[] }>()
  const other: MyWorkItem[] = []
  for (const it of items) {
    if (!it.vendorId) {
      other.push(it)
      continue
    }
    const g = groups.get(it.vendorId) ?? { key: it.vendorId, title: '', items: [] }
    if (!g.title && it.kind !== 'notice') g.title = it.title
    g.items.push(it)
    groups.set(it.vendorId, g)
  }
  for (const g of groups.values()) if (!g.title) g.title = 'Vendor'
  const out = [...groups.values()]
  if (other.length) out.push({ key: 'other', title: 'Not about one vendor', items: other })
  return out
}

// A row with a date is late by that date on the reader's own day (lib/days.ts)
// and never by the API's flag. The flag is set on the day in UTC: west of UTC
// it is a day ahead of the reader each evening, and it made a row due today
// read "0 days late"; in India it is a day behind until 05:30.
export function whenWords(it: MyWorkItem): { text: string; late: boolean } {
  if (it.dueOn) {
    const until = daysUntil(it.dueOn)
    if (until != null && until < 0) return { text: until === -1 ? '1 day late' : -until + ' days late', late: true }
    if (until === 0) return { text: 'Due today', late: false }
    return { text: 'Due ' + dayMonth(it.dueOn), late: false }
  }
  if (it.since) return { text: 'Since ' + dayMonth(it.since), late: false }
  return { text: '', late: false }
}

// Whether a row is late, for its chip and for anything that says what the chip
// says: by whenWords where the row has a date, so the chip and the count
// beside it cannot disagree, and by the API's flag only where it has none.
export function rowLate(it: MyWorkItem): boolean {
  return it.dueOn ? whenWords(it).late : Boolean(it.late)
}

// The one action on a row: the button that does the task here, or the link to
// where it is done. Rows carry ordinary buttons, not the filled primary: a
// list of thirteen filled buttons is a wall of orange in which nothing stands
// out, and each row has only the one action anyway.
export function RowAction({ it, onDone, onOpen }: { it: MyWorkItem; onDone: (key: string, what: string) => void; onOpen?: (key: string) => void }) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  if (it.action.kind === 'chase' && it.assessmentId) {
    return <ChaseButton assessmentId={it.assessmentId} actionId="tprm.my-work.a01" onChased={() => onDone(it.key, 'Reminder sent just now')} />
  }
  if (it.action.kind === 'mark-read' && it.noticeId) {
    const id = it.noticeId
    const mark = () => {
      if (busy) return
      setBusy(true)
      api
        .markNoticeRead(id)
        .then(() => {
          myWorkChanged()
          onDone(it.key, 'Marked as read')
        })
        .catch((err) => {
          setBusy(false)
          // Said, not swallowed: the notice stays unread and on the list.
          toast('The notice was not marked as read. ' + (err instanceof ApiError ? err.message : String(err)), 'warn')
        })
    }
    return (
      <span className="mw-acts">
        {it.action.href && (
          <a
            className="button"
            href={it.action.href}
            onClick={() => {
              onOpen?.(it.key)
              mark()
            }}
          >
            {taskVerb(it)}
          </a>
        )}
        <button type="button" className={it.action.href ? 'ghost' : undefined} disabled={busy} onClick={mark} data-action="tprm.my-work.a02">
          Mark as read
        </button>
      </span>
    )
  }
  return (
    <a className="button" href={it.action.href} aria-label={taskVerb(it) + ': ' + it.title} onClick={() => onOpen?.(it.key)}>
      {taskVerb(it)}
    </a>
  )
}

export function MyWorkList({
  items,
  onDone,
  done,
  onOpen,
  under,
}: {
  items: MyWorkItem[]
  // The group these rows are under: a row whose title is the group's name does
  // not say it again.
  under?: string
  onDone: (key: string, what: string) => void
  done: Record<string, string>
  // A row opened its task's screen: My work starts the "Next of N" queue.
  onOpen?: (key: string) => void
}) {
  return (
    <ul className="mw-list">
      {items.map((it) => {
        const when = whenWords(it)
        return (
          <li key={it.key} className={'mw-row' + (done[it.key] ? ' mw-done' : '')} data-task={it.key} data-kind={it.kind}>
            <span className="mw-chip">
              <StatusChip domain={it.status.domain} code={it.status.code} late={rowLate(it)} />
            </span>
            <span className="mw-what">
              <span className="mw-kind">{TASK_NOUN[it.kind] ?? 'Task'}</span>
              {it.title !== under && <b title={it.title}>{it.title}</b>}
              {it.what && <span className="mw-line">{it.what}</span>}
            </span>
            <span className={'mw-when' + (when.late ? ' mw-late' : '')}>{when.text}</span>
            <span className="mw-act">{done[it.key] ? <span className="mw-said" role="status">{done[it.key]}</span> : <RowAction it={it} onDone={onDone} onOpen={onOpen} />}</span>
          </li>
        )
      })}
    </ul>
  )
}

// What the screen holds: the filter on show, its rows so far, and the page
// that answered last (for the counts and the next page).
type Held = { show: Show; items: MyWorkItem[]; page: MyWorkPage }

export function MyWork({ me }: { me: Me }) {
  const [held, setHeld] = useState<Held | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [asked, setAsked] = useState<Show | null>(() => showOf(window.location.hash))
  const [more, setMore] = useState(false)
  const [byVendor, setByVendor] = useState(() => groupedOf(window.location.hash))
  // Opening My work again is a new look at the list: an earlier "Next of N" ends.
  useEffect(() => {
    endQueue()
  }, [])
  // What was done from a row on this visit. The row stays, quietly, so the list
  // does not jump under the pointer; the next read leaves it out.
  const [done, setDone] = useState<Record<string, string>>({})
  const ask = useRef(0)

  const read = useCallback(
    (want: Show | null) => {
      const n = ++ask.current
      const first = want ?? PERSONA_SHOW[me.persona?.code ?? ''] ?? 'all'
      api
        .myWork({ show: first, limit: PAGE })
        .then(async (page) => {
          // Opened without a filter: the job's own, unless it holds nothing.
          if (want == null && first !== 'all' && defaultShow(me.persona?.code, showCounts(page)) === 'all') {
            return { show: 'all' as Show, page: await api.myWork({ show: 'all', limit: PAGE }) }
          }
          return { show: first, page }
        })
        .then(({ show, page }) => {
          if (n !== ask.current) return
          countFrom(Promise.resolve(page))
          setHeld({ show, items: uniqueTasks(page.items ?? []), page })
          setError(null)
        })
        .catch((err) => {
          if (n === ask.current) setError(err)
        })
    },
    [me.persona?.code],
  )

  useEffect(() => {
    read(asked)
  }, [read, asked])

  useEffect(() => {
    const sync = () => {
      setAsked(showOf(window.location.hash))
      setByVendor(groupedOf(window.location.hash))
    }
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])

  const showMore = () => {
    if (!held || !held.page.next || more) return
    const n = ask.current
    setMore(true)
    api
      .myWork({ show: held.show, limit: PAGE, after: held.page.next })
      .then((page) => {
        if (n !== ask.current) return
        setHeld((h) => (h ? { show: h.show, items: uniqueTasks([...h.items, ...(page.items ?? [])]), page } : h))
      })
      .catch((err) => setError(err))
      .finally(() => setMore(false))
  }

  if (error != null && !held) return <ErrorBox error={error} what="My work" />
  if (!held)
    return (
      <div className="p1-page mw-page">
        <div className="p1-head">
          <h1>My work</h1>
        </div>
        <Skeleton lines={6} height={18} />
      </div>
    )

  const { show: current, items, page } = held
  const counts = showCounts(page)
  const choose = (s: Show) => {
    writeShow(s, byVendor)
    setAsked(s)
  }
  const group = (on: boolean) => {
    // The filter asked for, which may still be loading, not the one on screen.
    writeShow(asked ?? current, on)
    setByVendor(on)
  }
  // Opening a row keeps the list as it stands, so the task's screen can offer
  // the next one (lib/workQueue.ts); grouped, the queue follows the groups.
  const ordered = byVendor ? vendorGroups(items).flatMap((g) => g.items) : items
  const open = (key: string) => {
    startQueue(ordered, key, window.location.hash || '#/my-work', me.subject)
  }
  const notices = current === 'notice'
  const unlisted = notices ? page.matched - items.length : 0

  return (
    <div className="p1-page mw-page">
      <div className="p1-head">
        <div>
          <h1>My work</h1>
          <p className="mw-sub">
            {counts.all === 0 ? 'Nothing waiting for you' : WAITING_FOR_YOU + ': ' + counts.all}
            {page.persona?.name ? ', as ' + page.persona.name : ''}
          </p>
        </div>
      </div>

      <span className="hm-seg mw-seg" role="group" aria-label="Show">
        {SHOWS.filter((s) => s === 'all' || counts[s] > 0 || s === current).map((s) => (
          <button key={s} type="button" aria-pressed={current === s} onClick={() => choose(s)}>
            {SHOW_LABEL[s]}
            <b>{counts[s]}</b>
          </button>
        ))}
      </span>
      {!notices && (byVendor || items.length > 1) && (
        <button type="button" className="ghost mw-toggle" aria-pressed={byVendor} onClick={() => group(!byVendor)}>
          By vendor
        </button>
      )}

      <section className="panel mw-panel" aria-label={SHOW_LABEL[current]}>
        {items.length === 0 ? (
          <div className="mw-empty">
            <strong>{notices ? 'No unread notices' : counts.all === 0 ? 'Nothing waiting for you' : 'Nothing here'}</strong>
            <span>
              {notices
                ? 'News about your vendors appears here until you mark it read.'
                : counts.all === 0
                  ? 'Reviews, decisions and sign-offs appear here when they are yours.'
                  : 'Choose another filter above.'}
            </span>
          </div>
        ) : (
          byVendor && !notices ? (
            vendorGroups(items).map((g) => (
              <div className="mw-group" key={g.key}>
                <h2 className="mw-group-head">
                  {g.title} <span>{g.items.length}</span>
                </h2>
                <MyWorkList items={g.items} under={g.title} done={done} onOpen={open} onDone={(key, what) => setDone((d) => ({ ...d, [key]: what }))} />
              </div>
            ))
          ) : (
            <MyWorkList items={items} done={done} onOpen={open} onDone={(key, what) => setDone((d) => ({ ...d, [key]: what }))} />
          )
        )}
        {page.next && (
          <p className="mw-more">
            <button type="button" disabled={more} onClick={showMore}>
              {more ? 'Loading' : 'Show more'}
            </button>
            <span>
              {items.length} of {page.matched} shown
            </span>
          </p>
        )}
        {unlisted > 0 && (
          <p className="mw-more">
            <span>
              The newest {items.length} of {page.matched} unread notices. Marking these read brings the older ones up.
            </span>
          </p>
        )}
      </section>
    </div>
  )
}
